import { useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Plus, X } from "lucide-react";
import { mediaSrc } from "@/lib/media";
import { GuidePhotoPositioner } from "./GuidePhotoPositioner";
import { StarPicker } from "./Rating";
const useEscapeKey = (active, onClose) => {
  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active, onClose]);
};


const REG_FIELDS = [
  { k: "name", label: "Name" },
  { k: "mobile", label: "Mobile" },
  { k: "whatsapp", label: "WhatsApp" },
  { k: "email", label: "Email" },
  { k: "profession", label: "Profession" },
  { k: "place", label: "Place" },
  { k: "program", label: "Program Name" },
  { k: "batch", label: "Batch#" },
];

export const EditRegistrationModal = ({ editing, onClose, editForm, setEditForm, saveEdit, saving, fields = REG_FIELDS, title = "Edit registration", testId = "edit-modal" }) => {
  useEscapeKey(!!editing, onClose);
  return editing && (
  <div className="fixed inset-0 z-[80] grid place-items-center p-6" onClick={onClose}>
    <div className="absolute inset-0 bg-[#0F1B16]/40 backdrop-blur-sm" />
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      onClick={(e) => e.stopPropagation()} data-testid={testId}
      className="relative bg-background border border-border rounded-3xl shadow-2xl w-full max-w-lg p-8"
    >
      <button onClick={onClose} className="absolute top-5 right-5 grid place-items-center w-9 h-9 rounded-full border border-border hover:bg-muted transition-colors"><X size={16} /></button>
      <h3 className="font-serif text-3xl mb-6">{title}</h3>
      <form onSubmit={saveEdit} className="grid sm:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.k} className={f.k === "program" ? "sm:col-span-2" : ""}>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">{f.label}</label>
            <input
              data-testid={`${testId === "edit-modal" ? "edit" : testId}-${f.k}`} value={editForm[f.k] || ""}
              onChange={(e) => setEditForm({ ...editForm, [f.k]: e.target.value })}
              className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors"
            />
          </div>
        ))}
        <div className="sm:col-span-2 flex justify-end gap-3 mt-2">
          <button type="button" onClick={onClose} className="rounded-full border border-border px-6 py-2.5 text-sm hover:bg-muted transition-colors">Cancel</button>
          <button type="submit" disabled={saving} data-testid={`${testId === "edit-modal" ? "edit" : testId}-save-btn`} className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </motion.div>
  </div>
  );
};

export const TestimonialEditModal = ({
  editingT,
  onClose,
  tEditForm,
  setTEditForm,
  tEditImages,
  setTEditImages,
  tEditDragIdx,
  setTEditDragIdx,
  moveTEditImage,
  addEditPhotos,
  tEditUploading,
  saveEditT,
}) => {
  useEscapeKey(!!editingT, onClose);
  return editingT && (
  <div className="fixed inset-0 z-[80] grid place-items-center p-6" onClick={onClose}>
    <div className="absolute inset-0 bg-[#0F1B16]/40 backdrop-blur-sm" />
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      onClick={(e) => e.stopPropagation()} data-testid="testimonial-edit-modal"
      className="relative bg-background border border-border rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-8"
    >
      <button onClick={onClose} className="absolute top-5 right-5 grid place-items-center w-9 h-9 rounded-full border border-border hover:bg-muted transition-colors"><X size={16} /></button>
      <h3 className="font-serif text-3xl mb-6">Edit testimonial</h3>
      <form onSubmit={saveEditT} className="space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Name</label>
          <input data-testid="testimonial-edit-name" value={tEditForm.name} onChange={(e) => setTEditForm({ ...tEditForm, name: e.target.value })}
            placeholder="Participant name"
            className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors" />
        </div>
        {(!editingT.kind || editingT.kind === "text" || editingT.kind === "image") && (
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">{editingT.kind === "image" ? "Caption (optional)" : "Testimonial"}</label>
            <textarea rows={4} data-testid="testimonial-edit-text" value={tEditForm.text} onChange={(e) => setTEditForm({ ...tEditForm, text: e.target.value })}
              placeholder={editingT.kind === "image" ? "Optional caption / short quote shown under the photo…" : "Write the testimonial…"}
              className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors resize-none" />
          </div>
        )}
        {editingT.kind === "image" && (
          <div data-testid="testimonial-edit-photos">
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Photos {tEditImages.length > 1 && <span className="normal-case tracking-normal text-[11px]">· drag to reorder</span>}</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {tEditImages.map((u, idx) => (
                <div
                  key={u + idx} draggable
                  onDragStart={() => setTEditDragIdx(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => moveTEditImage(tEditDragIdx, idx)}
                  data-testid={`testimonial-edit-photo-${idx}`}
                  className={`relative w-20 h-20 rounded-lg overflow-hidden border cursor-move group ${tEditDragIdx === idx ? "border-accent opacity-60" : "border-border"}`}
                >
                  <img src={mediaSrc(u)} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover pointer-events-none" />
                  <span className="absolute top-0.5 left-0.5 grid place-items-center min-w-[1rem] h-4 px-1 rounded bg-black/60 text-white text-[10px]">{idx + 1}</span>
                  <button type="button" onClick={() => setTEditImages((imgs) => imgs.filter((_, i) => i !== idx))} title="Remove" className="absolute top-0.5 right-0.5 grid place-items-center w-5 h-5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={11} />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border border-dashed border-border grid place-items-center cursor-pointer hover:border-accent hover:bg-accent/5 transition-colors" title="Add photos">
                <input type="file" accept="image/*" multiple data-testid="testimonial-edit-add-photos" className="hidden"
                  onChange={(e) => { addEditPhotos(Array.from(e.target.files || [])); e.target.value = ""; }} />
                {tEditUploading ? <span className="text-[10px] text-muted-foreground">…</span> : <Plus size={18} className="text-muted-foreground" />}
              </label>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Rating</span>
            <StarPicker testid="testimonial-edit-rating" value={tEditForm.rating} onChange={(v) => setTEditForm({ ...tEditForm, rating: v })} />
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="testimonial-edit-featured" checked={tEditForm.featured} onChange={(e) => setTEditForm({ ...tEditForm, featured: e.target.checked })} className="w-4 h-4 accent-[#1A2E26]" />
            Feature at top
          </label>
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full border border-border px-6 py-2.5 text-sm hover:bg-muted transition-colors">Cancel</button>
          <button type="submit" data-testid="testimonial-edit-save" className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-transform duration-300">Save changes</button>
        </div>
      </form>
    </motion.div>
  </div>
  );
};

export const FeedbackEditModal = ({ editingFb, onClose, fbEditForm, setFbEditForm, saveEditFb }) => {
  useEscapeKey(!!editingFb, onClose);
  return editingFb && (
  <div className="fixed inset-0 z-[80] grid place-items-center p-6" onClick={onClose}>
    <div className="absolute inset-0 bg-[#0F1B16]/40 backdrop-blur-sm" />
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      onClick={(e) => e.stopPropagation()} data-testid="feedback-edit-modal"
      className="relative bg-background border border-border rounded-3xl shadow-2xl w-full max-w-lg p-8"
    >
      <button onClick={onClose} className="absolute top-5 right-5 grid place-items-center w-9 h-9 rounded-full border border-border hover:bg-muted transition-colors"><X size={16} /></button>
      <h3 className="font-serif text-3xl mb-6">Edit feedback</h3>
      <form onSubmit={saveEditFb} className="space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Name</label>
          <input data-testid="feedback-edit-name" value={fbEditForm.name} onChange={(e) => setFbEditForm({ ...fbEditForm, name: e.target.value })}
            className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Message</label>
          <textarea rows={4} data-testid="feedback-edit-message" value={fbEditForm.message} onChange={(e) => setFbEditForm({ ...fbEditForm, message: e.target.value })}
            className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors resize-none" />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full border border-border px-6 py-2.5 text-sm hover:bg-muted transition-colors">Cancel</button>
          <button type="submit" data-testid="feedback-edit-save" className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-transform duration-300">Save changes</button>
        </div>
      </form>
    </motion.div>
  </div>
  );
};

export const GuideEditModal = ({ editingG, onClose, gEditForm, setGEditForm, gEditFile, setGEditFile, saveEditG }) => {
  useEscapeKey(!!editingG, onClose);
  return editingG && (
  <div className="fixed inset-0 z-[80] grid place-items-center p-6" onClick={onClose}>
    <div className="absolute inset-0 bg-[#0F1B16]/40 backdrop-blur-sm" />
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      onClick={(e) => e.stopPropagation()} data-testid="guide-edit-modal"
      className="relative bg-background border border-border rounded-3xl shadow-2xl w-full max-w-lg p-8"
    >
      <button onClick={onClose} className="absolute top-5 right-5 grid place-items-center w-9 h-9 rounded-full border border-border hover:bg-muted transition-colors"><X size={16} /></button>
      <h3 className="font-serif text-3xl mb-6">Edit guide</h3>
      <form onSubmit={saveEditG} className="space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Replace photo</label>
          <input
            type="file"
            data-testid="guide-edit-photo"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setGEditFile(file);
              if (file) setGEditForm({ ...gEditForm, photo_position_x: 50, photo_position_y: 50, photo_zoom: 1 });
            }}
            className="mt-2 text-sm text-muted-foreground file:mr-3 file:rounded-full file:border-0 file:bg-primary file:text-primary-foreground file:px-4 file:py-2 file:text-sm"
          />
        </div>
        <GuidePhotoPositioner
          file={gEditFile}
          existingUrl={editingG.photo_url}
          value={gEditForm}
          onChange={(patch) => setGEditForm({ ...gEditForm, ...patch })}
          testid="guide-edit-crop"
        />
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Name</label>
          <input data-testid="guide-edit-name" value={gEditForm.name} onChange={(e) => setGEditForm({ ...gEditForm, name: e.target.value })}
            className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Profession</label>
            <input data-testid="guide-edit-profession" value={gEditForm.profession} onChange={(e) => setGEditForm({ ...gEditForm, profession: e.target.value })}
              className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Place</label>
            <input data-testid="guide-edit-place" value={gEditForm.place} onChange={(e) => setGEditForm({ ...gEditForm, place: e.target.value })}
              className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Phone number</label>
            <input type="tel" data-testid="guide-edit-phone" value={gEditForm.phone} onChange={(e) => setGEditForm({ ...gEditForm, phone: e.target.value })}
              className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Batch (optional)</label>
            <input data-testid="guide-edit-batch" value={gEditForm.batch} onChange={(e) => setGEditForm({ ...gEditForm, batch: e.target.value })}
              className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">How they help (bio)</label>
          <textarea rows={2} data-testid="guide-edit-bio" value={gEditForm.bio} onChange={(e) => setGEditForm({ ...gEditForm, bio: e.target.value })}
            className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors resize-none" />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full border border-border px-6 py-2.5 text-sm hover:bg-muted transition-colors">Cancel</button>
          <button type="submit" data-testid="guide-edit-save" className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-transform duration-300">Save changes</button>
        </div>
      </form>
    </motion.div>
  </div>
  );
};

export const AddRegistrationModal = ({ open, onClose, regForm, setRegForm, createRegistration, savingReg }) => {
  useEscapeKey(open, onClose);
  return open && (
  <div className="fixed inset-0 z-[80] grid place-items-center p-6" onClick={onClose}>
    <div className="absolute inset-0 bg-[#0F1B16]/40 backdrop-blur-sm" />
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      onClick={(e) => e.stopPropagation()} data-testid="add-reg-modal"
      className="relative bg-background border border-border rounded-3xl shadow-2xl w-full max-w-lg p-8"
    >
      <button onClick={onClose} className="absolute top-5 right-5 grid place-items-center w-9 h-9 rounded-full border border-border hover:bg-muted transition-colors"><X size={16} /></button>
      <h3 className="font-serif text-3xl mb-2">Add registration</h3>
      <p className="text-sm text-muted-foreground mb-6">Manually add a participant. This bypasses the seat limit.</p>
      <form onSubmit={createRegistration} className="grid sm:grid-cols-2 gap-5">
        {[
          { k: "name", label: "Name" },
          { k: "mobile", label: "Mobile" },
          { k: "email", label: "Email" },
          { k: "profession", label: "Profession" },
          { k: "place", label: "Place" },
          { k: "program", label: "Program Name" },
          { k: "batch", label: "Batch#" },
        ].map((f) => (
          <div key={f.k} className={f.k === "program" ? "sm:col-span-2" : ""}>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">{f.label}{f.k === "name" ? " *" : ""}</label>
            <input
              data-testid={`add-reg-${f.k}`} value={regForm[f.k] || ""}
              onChange={(e) => setRegForm({ ...regForm, [f.k]: e.target.value })}
              className="w-full mt-2 bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors"
            />
          </div>
        ))}
        <div className="sm:col-span-2 flex justify-end gap-3 mt-2">
          <button type="button" onClick={onClose} className="rounded-full border border-border px-6 py-2.5 text-sm hover:bg-muted transition-colors">Cancel</button>
          <button type="submit" disabled={savingReg} data-testid="add-reg-save-btn" className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
            {savingReg ? "Adding…" : "Add registration"}
          </button>
        </div>
      </form>
    </motion.div>
  </div>
  );
};
